import { styled } from '@mui/material/styles';
import { TextareaAutosize } from '@mui/material';
import { useEffect, useRef } from 'react';

interface StyledTextAreaProps {
  width?: string;
  placeholderAlign?: string;
  minRows?: number;
  maxRows?: number | undefined;
  value: string;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  borderStyle?: string;
  isFocused?: boolean;
  margin?: string;
  disabled?: boolean;
}

const StyledTextareaAutosize = styled(TextareaAutosize, {
  shouldForwardProp: (prop) => !['width', 'placeholderAlign', 'borderStyle', 'margin'].includes(prop as string)
})<{
  width?: string;
  placeholderAlign?: string;
  borderStyle?: string;
  margin?: string;
}>(({ theme, width = '100%', placeholderAlign = 'left', borderStyle = 'solid', margin = '0' }) => ({
  width,
  resize: 'none',
  padding: '16px',
  border: `1px ${borderStyle} #D3D3D3`,
  borderRadius: '4px',
  fontSize: '12px',
  outline: 'none',
  color: '#161616',
  margin: margin,
  '&::placeholder': {
    color: '#161616',
    fontStyle: 'normal',
    opacity: 1,
    width: '100%',
    height: '0',
    textAlign: placeholderAlign,
    fontSize: '12px',
    fontWeight: '400'
  },
  '&:not(:empty)': {
    borderColor: theme?.palette?.primary?.main || '#D3D3D3'
  }
}));

export const StyledTextArea = ({
  width,
  placeholderAlign,
  minRows = 1,
  maxRows = undefined,
  value,
  onChange,
  placeholder = '',
  borderStyle,
  isFocused = false,
  margin,
  disabled = false
}: StyledTextAreaProps) => {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (isFocused) {
      textareaRef.current?.focus();
    }
  }, [isFocused]);

  return (
    <StyledTextareaAutosize
      ref={textareaRef}
      width={width}
      placeholderAlign={placeholderAlign}
      borderStyle={borderStyle}
      minRows={minRows}
      maxRows={maxRows}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      margin={margin}
      disabled={disabled}
    />
  );
};
